/**
 * Utility functions for JWT token handling
 */

export interface JWTRole {
  id: string;
  name: string;
  description: string;
}

export interface JWTMetadata {
  firstName: string;
  lastName: string;
  status: 'active' | 'inactive' | 'suspended';
  isOnboarded: boolean;
}

export interface JWTPayload {
  exp: number;
  iat: number;
  userId: string;
  businessId: string;
  email: string;
  roles: JWTRole[];
  permissions: Record<string, boolean>;
  firebaseUid?: string;
  metadata: JWTMetadata;
  type?: string;
  tokenVersion?: number;
  [key: string]: unknown;
}

export interface ExtractedTokenData {
  userId: string;
  businessId: string;
  email: string;
  roles: JWTRole[];
  permissions: Record<string, boolean>;
  metadata: JWTMetadata;
  isOnboarded: boolean;
}

/**
 * Decode a JWT token payload without verification
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @param bufferMinutes - Buffer time in minutes to consider token as expired (default: 1)
 * @returns true if token is expired or will expire within buffer time
 */
export function isTokenExpired(
  token: string,
  bufferMinutes: number = 1
): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  const bufferTime = bufferMinutes * 60;

  return payload.exp <= currentTime + bufferTime;
}

/**
 * Check if a JWT token is a refresh token
 * @param token - JWT token string
 * @returns true if token is a refresh token
 */
export function isRefreshToken(token: string): boolean {
  const payload = decodeJWT(token);
  return payload?.type === "refresh";
}

/**
 * Get token expiration time as a Date object
 * @param token - JWT token string
 * @returns Date object or null if invalid
 */
export function getTokenExpiry(token: string): Date | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return null;

  return new Date(payload.exp * 1000);
}

/**
 * Get time until token expires
 * @param token - JWT token string
 * @returns Time in seconds until expiry, or 0 if expired
 */
export function getTimeUntilExpiry(token: string): number {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return 0;

  const currentTime = Math.floor(Date.now() / 1000);
  return Math.max(0, payload.exp - currentTime);
}

/**
 * Extract RBAC-related data from JWT token
 * @param token - JWT token string
 * @returns Extracted token data or null if invalid
 */
export function extractTokenData(token: string): ExtractedTokenData | null {
  const payload = decodeJWT(token);
  if (!payload) return null;

  return {
    userId: payload.userId || '',
    businessId: payload.businessId || '',
    email: payload.email || '',
    roles: payload.roles || [],
    permissions: payload.permissions || {},
    metadata: payload.metadata || {
      firstName: '',
      lastName: '',
      status: 'active',
      isOnboarded: false,
    },
    isOnboarded: payload.metadata?.isOnboarded ?? false,
  };
}

/**
 * Check if user has a specific permission from token
 * @param token - JWT token string
 * @param permissionKey - Permission key to check (e.g., 'teams.view')
 * @returns true if user has the permission
 */
export function hasPermissionFromToken(token: string, permissionKey: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.permissions) return false;

  return payload.permissions[permissionKey] === true;
}

/**
 * Check if user has a specific role from token
 * @param token - JWT token string
 * @param roleName - Role name to check (e.g., 'admin')
 * @returns true if user has the role
 */
export function hasRoleFromToken(token: string, roleName: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.roles) return false;

  return payload.roles.some(role =>
    role.name.toLowerCase() === roleName.toLowerCase()
  );
}

/**
 * Get all permission keys from token
 * @param token - JWT token string
 * @returns Array of permission keys that are true
 */
export function getPermissionsFromToken(token: string): string[] {
  const payload = decodeJWT(token);
  if (!payload || !payload.permissions) return [];

  return Object.entries(payload.permissions)
    .filter(([, value]) => value === true)
    .map(([key]) => key);
}

/**
 * Get all role names from token
 * @param token - JWT token string
 * @returns Array of role names
 */
export function getRolesFromToken(token: string): string[] {
  const payload = decodeJWT(token);
  if (!payload || !payload.roles) return [];

  return payload.roles.map(role => role.name);
}
